import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import type { CallbackInterface } from 'recoil';
import { useOnUnload } from 'hooks/useOnUnload';
import { useMutation, useApolloClient } from '@apollo/client';
import clone from 'ramda/es/clone';
import {
  steppedHPConfigurations,
  clinicalStepsBaseline,
  hangingProtocolStepIndex,
  hangingProtocolSubStepIndex,
  isCreatingStepState,
  getStepsFromVersioned,
  makeVersionedSteps,
} from '../state';
import type { VersionedStepConfigurations } from '../state';
import {
  beginPendingStepCreate,
  beginPendingStepDelete,
  clearPendingStepCreates,
  endPendingStepDelete,
  endPendingStepCreate,
} from '../pendingStepMutations';
import { stepToGqlInput, toAllowedViewportType } from '../steppedHPTypes';
import type { StepConfiguration } from '../steppedHPTypes';
import { stepViewportConfigsToDehydrated, stepLayoutsToLayouts } from './useStepFormSync';
import { isStepEqual } from './useClinicalStepDirtyState';
import {
  UPDATE_HANGING_PROTOCOL,
  UPDATE_HANGING_PROTOCOL_STEP,
  CREATE_HANGING_PROTOCOL_STEP,
  DELETE_HANGING_PROTOCOL_STEP,
  HANGING_PROTOCOL,
  GET_BASE_VIEWER_DATA,
} from 'modules/Apollo/queries';
import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import {
  deletePrecacheQuery,
  updatePrecacheQuery,
} from 'modules/Apollo/precache/precacheOperation';
import { logger } from 'modules/logger';
import type { HangingProtocolPayload } from 'generated/graphql';
import {
  createDefaultStep,
  generateDefaultStepName,
  isClientGeneratedSmid,
  reconcileStepSmids,
} from '../hpStepController';
import { createStepToastContentComponent, getDefaultHPToastOptions } from '../Steps/StepToast';
import { HP_TOAST_UNDO_DURATION } from '../Steps/hpStepToastMessages';
import {
  CLINICAL_REORDER_FAILED_TOAST,
  CLINICAL_SAVE_FAILED_TOAST,
  CLINICAL_STEP_CREATED_TOAST,
  CLINICAL_STEP_CREATED_WITH_REVERT_TOAST,
  CLINICAL_STEP_REVERTED_TOAST,
  CLINICAL_STEP_SAVED_TOAST,
  CREATE_FAILED_TOAST,
  DELETE_FAILED_TOAST,
} from '../Steps/stepsToastFactory';
import { composeHPFailureMessage } from './extractHPBackendErrorMessage';
import {
  viewportsConfigurationsState,
  dehydratedHangingProtocol,
  activeHangingProtocolSmidState,
} from '../../ViewportsConfigurations/state';
import { clearViewportOverrides } from '../../ViewportsConfigurations/manipulators';
import type { DehydratedViewportsConfigurations } from '../../ViewportsConfigurations/types';
import { applyProtocolLayouts } from '../../Viewer/StudyLoader/viewerLayoutWriters';
import { layoutsForAllCasesState } from '../../Viewer/StudyLoader/viewerLoaderState';
import type { Layouts } from '../../Viewer/StudyLoader/viewerLoaderState';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';

/**
 * Shape of a precached getBaseViewerData response that contains a
 * matched hanging protocol with steps.
 */
interface CachedMatchedHP {
  smid?: string;
  steps?: Array<{ smid?: string } & Record<string, unknown>>;
}
interface CachedBaseViewerData {
  data?: {
    matchHangingProtocolByWorklistsAndStudies?: CachedMatchedHP | null;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrow an opaque precache transform payload (`updatePrecacheQuery` passes the parsed JSON as
 * `unknown`) down to the matched-HP node, validating each level at runtime rather than asserting
 * the whole shape. Returns `undefined` when the payload isn't the expected getBaseViewerData shape.
 */
function getMatchedHPFromCache(data: unknown): CachedMatchedHP | undefined {
  if (!isRecord(data) || !isRecord(data.data)) return undefined;
  const hp = data.data.matchHangingProtocolByWorklistsAndStudies;
  return isRecord(hp) ? (hp as CachedMatchedHP) : undefined;
}

/**
 * Append a step to the cached getBaseViewerData response for the given HP.
 * Returns `null` if the cache entry doesn't match the target HP or has no
 * steps array, signalling the caller to leave the entry unchanged.
 */
function appendStepToMatchedHP(
  data: unknown,
  hpSmid: string,
  step: Record<string, unknown>
): unknown | null {
  const typed = data as CachedBaseViewerData;
  const hp = getMatchedHPFromCache(data);
  if (!hp || hp.smid !== hpSmid || !Array.isArray(hp.steps)) return null;
  // Skip if the step is already present (e.g. duplicate write from another tab)
  if (hp.steps.some((s) => s?.smid === step.smid)) return null;
  return {
    ...typed,
    data: {
      ...typed.data,
      matchHangingProtocolByWorklistsAndStudies: {
        ...hp,
        steps: [...hp.steps, step],
      },
    },
  };
}

/**
 * Reads a HangingProtocol from the Apollo cache and builds the base
 * HangingProtocolPayload fields (everything except `steps`).
 * Returns null on cache miss.
 */
function readHPPayloadBaseFromCache(
  apolloClient: ReturnType<typeof useApolloClient>,
  hpSmid: string
): Omit<HangingProtocolPayload, 'steps'> | null {
  const cachedHP = apolloClient.readFragment({
    id: `HangingProtocol:${hpSmid}`,
    fragment: HANGING_PROTOCOL,
  });

  if (!cachedHP) return null;

  return {
    name: cachedHP.name,
    studyDescriptions: cachedHP.studyDescriptions ?? [],
    numberOfPriors: cachedHP.numberOfPriors ?? 0,
    data: {
      viewportsConfigurations:
        cachedHP.viewportsConfigurations?.map(
          (vc: { id: string; configuration: Record<string, unknown> }) => ({
            id: vc.id,
            ...vc.configuration,
          })
        ) ?? [],
      layouts:
        cachedHP.data?.layouts?.map((l: { id: string; layout: number[] }) => ({
          id: l.id,
          layout: l.layout,
        })) ?? [],
    },
  };
}

/**
 * Propagate a persisted step change into the two HP caches a page load reads from: the Apollo
 * `HangingProtocol` fragment and the `getBaseViewerData` precache entry.
 *
 * Both save paths need this. The deferred path always did it; the immediate path (renames, which
 * auto-save with no undo window) did not, so a rename reached the backend but the precached
 * base-viewer response kept the old `display_name` and the next load rehydrated the stale name
 * (VX-4748). Shared so the two paths cannot drift again.
 *
 * Best-effort by design: a cache miss or write failure must not fail an already-persisted save.
 */
async function syncPersistedStepIntoHPCaches(
  apolloClient: ReturnType<typeof useApolloClient>,
  hpSmid: Nullable<string>,
  stepSmid: Nullable<string>,
  gqlPayload: ReturnType<typeof stepToGqlInput>
): Promise<void> {
  if (hpSmid && stepSmid != null) {
    try {
      const cachedHP = apolloClient.readFragment({
        id: `HangingProtocol:${hpSmid}`,
        fragment: HANGING_PROTOCOL,
      });
      if (cachedHP?.steps) {
        const updatedSteps = cachedHP.steps.map((cachedStep: { smid: string }) =>
          cachedStep.smid === stepSmid ? { ...cachedStep, ...gqlPayload } : cachedStep
        );
        apolloClient.writeFragment({
          id: `HangingProtocol:${hpSmid}`,
          fragment: HANGING_PROTOCOL,
          data: { ...cachedHP, steps: updatedSteps },
        });
      }
    } catch {
      // Best-effort cache update.
    }
  }

  await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });
}

/** Pre-revert ("dirty") step snapshot captured so UNDO can re-apply it. */
export interface DirtyStepSnapshot {
  stepIndex: number;
  currentCaseId: Nullable<string>;
  modifiedStep: StepConfiguration | null;
  modifiedViewports: DehydratedViewportsConfigurations | null;
  modifiedLayouts: Layouts | null;
}

/**
 * Re-apply a step's pre-revert snapshot to the viewer atoms. Shared by the UNDO action of both
 * `revertStep` and `createStepWithRevert`: it restores the captured step config, viewport
 * overrides, and layouts that were cleared when the step reverted to baseline. Exported for unit
 * testing. No-ops for any snapshot field that wasn't captured (null).
 */
export function restoreDirtyStepSnapshot(
  set: CallbackInterface['set'],
  { stepIndex, currentCaseId, modifiedStep, modifiedViewports, modifiedLayouts }: DirtyStepSnapshot
): void {
  if (modifiedStep) {
    set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
      const steps = prev.steps?.map((s, i) => (i === stepIndex ? modifiedStep : s));
      return makeVersionedSteps(steps ?? null, prev);
    });
  }
  if (currentCaseId && modifiedViewports) {
    set(viewportsConfigurationsState(currentCaseId), (prev) => ({
      ...prev,
      data: modifiedViewports,
    }));
  }
  if (currentCaseId && modifiedLayouts) {
    // The undone layout still reaches the screen without the user override: modifiedStep carries it
    // into the step atom above, and the protocol layer is re-derived per step index on navigation.
    applyProtocolLayouts(set, { caseId: currentCaseId, layouts: modifiedLayouts });
  }
}

// Module-level maps so that pending operations survive StepBar unmount/remount
// (e.g. when the HP edit sidebar opens, ResizableStepBar temporarily remounts StepBar).
const pendingDeletions = new Map<string, ReturnType<typeof setTimeout>>();
// Baseline entry captured for each pending deletion, so an undo can restore the exact
// pre-delete dirty state (a step with unsaved edits stays dirty; a saved step stays clean).
// `null` means the step had no baseline entry (e.g. it was never saved).
const pendingDeletionBaselines = new Map<string, StepConfiguration | null>();
const pendingSaves = new Map<
  string,
  {
    timer: ReturnType<typeof setTimeout>;
    payload: ReturnType<typeof stepToGqlInput>;
    // Baseline snapshot captured before the optimistic save advanced it. Used
    // to roll the baseline back if the deferred mutation fails, so the step
    // returns to a dirty state the user can retry.
    rollbackStep: StepConfiguration | null;
  }
>();
// Keyed by tempSmid so concurrent creates (rapid double-add during the undo
// window) are each individually cancellable, matching pendingSaves/pendingDeletions.
// A single-slot variable would let a second create clobber the first's timer
// reference, breaking the toast onClose/onAction guards and the cleanup effect.
const pendingCreate = new Map<
  string,
  {
    timer: ReturnType<typeof setTimeout>;
    hpSmid: string;
    // Step index before the optimistic add, so a failed create can restore it (VX-4441).
    preCreateIndex: number;
  }
>();

/**
 * Undoes an optimistic step create completely: cancels the deferred backend write and removes the
 * step from both atoms that were given a copy of it.
 *
 * An optimistic create leaves the new step in three places -- `steppedHPConfigurations`, the clinical
 * baseline, and a `pendingCreate` timer -- and anything that discards it has to undo all three. The
 * toast's UNDO action did; Reset All did none of them, so the step survived the reset (the baseline it
 * restores from held a copy) and the create landed seconds later on top of the refetched server state,
 * leaving two chips with one name (VX-4811).
 *
 * Extracted so the discard paths cannot drift apart again. Both callers now share one definition of
 * "undo this create" rather than each remembering three steps, which is the property that was missing:
 * the sibling layout defect became four tickets (VX-4624, VX-4732, VX-4768, VX-4772) for exactly this
 * reason, and VX-4740 was this shape with a different queue.
 *
 * Returns the temp smids it discarded so a caller already holding a baseline snapshot can filter them
 * out of it -- a `set` here does not retroactively change a value the caller has already read.
 */
function discardPendingCreates(
  set: CallbackInterface['set'],
  tempSmids: ReadonlyArray<string>,
  onDiscarded: (count: number) => void
): ReadonlySet<string> {
  const discarded = new Set<string>();
  for (const tempSmid of tempSmids) {
    const pending = pendingCreate.get(tempSmid);
    if (pending == null) continue;
    clearTimeout(pending.timer);
    pendingCreate.delete(tempSmid);
    endPendingStepCreate(tempSmid);
    discarded.add(tempSmid);
  }
  if (discarded.size === 0) return discarded;

  set(steppedHPConfigurations, (prev: VersionedStepConfigurations) =>
    makeVersionedSteps(prev.steps?.filter((s) => !discarded.has(s.smid ?? '')) ?? null, prev)
  );
  set(clinicalStepsBaseline, (prev) => prev?.filter((s) => !discarded.has(s.smid ?? '')) ?? null);
  onDiscarded(discarded.size);
  return discarded;
}

export function useClinicalStepOperations() {
  const apolloClient = useApolloClient();
  // Recoil setters are stable across renders, so calling this directly (including from inside
  // useRecoilCallback bodies below) is safe. Only *reads* of baseline are closure/stale-value
  // risks -- those must go through `snapshot.getLoadable` inside a useRecoilCallback instead of
  // closing over a value read here.
  const setBaseline = useSetRecoilState(clinicalStepsBaseline);
  const [updateHPMutation] = useMutation(UPDATE_HANGING_PROTOCOL, {
    context: { telemetry: { component: 'useClinicalStepOperations' } },
  });
  const [updateStepMutation] = useMutation(UPDATE_HANGING_PROTOCOL_STEP, {
    context: { telemetry: { component: 'useClinicalStepOperations' } },
  });
  const [createStepMutation] = useMutation(CREATE_HANGING_PROTOCOL_STEP, {
    context: { telemetry: { component: 'useClinicalStepOperations' } },
  });
  const [deleteStepMutation] = useMutation(DELETE_HANGING_PROTOCOL_STEP, {
    context: { telemetry: { component: 'useClinicalStepOperations' } },
  });
  const { enqueueToast, closeToast } = useToasterDispatch();

  // Track the number of in-flight backend mutations so the UI can show a sync indicator.
  const [pendingOperations, setPendingOperations] = useState(0);
  // Track deferred saves waiting for the undo window to expire. Drives the
  // "pending" spinner before the actual mutation fires.
  const [deferredSaveCount, setDeferredSaveCount] = useState(0);
  const trackOperation = useCallback(async <T>(operation: Promise<T>): Promise<T> => {
    setPendingOperations((n) => n + 1);
    try {
      return await operation;
    } finally {
      setPendingOperations((n) => n - 1);
    }
  }, []);

  const { currentCaseId } = useCurrentCaseContext();

  // Clear pending timer maps when the HP changes or the hook unmounts.
  // Stale timers from a previous HP should not fire against the new one.
  const hpSmid = useRecoilValue(activeHangingProtocolSmidState);
  useEffect(() => {
    return () => {
      for (const timer of pendingDeletions.values()) clearTimeout(timer);
      pendingDeletions.clear();
      pendingDeletionBaselines.clear();
      for (const { timer } of pendingSaves.values()) clearTimeout(timer);
      pendingSaves.clear();
      for (const { timer } of pendingCreate.values()) clearTimeout(timer);
      pendingCreate.clear();
    };
  }, [hpSmid]);

  // Drop the pending-create guard when the active HP actually changes. A create still in flight
  // belongs to the HP the user just left, and `useHangingProtocol` arms `__lastInitializedHPKey`
  // before it calls `applyFetchedSteps` -- so a guard left set across the switch does not delay the
  // new HP's initialization, it cancels it, and nothing retries.
  //
  // Deliberately in the effect body behind a previous-value ref rather than in the cleanup above:
  // `useClinicalStepOperations` is mounted in more than one place (StepBar and
  // HangingProtocolWidget) and this registry is module-level, so a cleanup would let either instance
  // unmounting mid-create clear the guard the other still needs -- reopening VX-4811. Running on
  // mount would do the same on a StepBar remount.
  // Seeded with the current smid rather than null on purpose, and named for what it holds rather
  // than for the usual previous/current pattern: there is nothing to clear on the first render, and
  // a null seed would make the mount itself look like a switch and drop a guard the other mounted
  // instance may still need.
  const lastClearedHpSmidRef = useRef(hpSmid);
  useEffect(() => {
    if (lastClearedHpSmidRef.current === hpSmid) return;
    lastClearedHpSmidRef.current = hpSmid;
    clearPendingStepCreates();
  }, [hpSmid]);

  /**
   * Revert a single step to its baseline snapshot.
   *
   * In addition to restoring the step atom, this also clears user viewport
   * overrides and writes the baseline step's dehydrated viewport configs
   * directly to the dehydratedHangingProtocol atom so the viewport selector
   * reflects the revert immediately (the effect-based chain fires after
   * paint). This is the fallback path when useClinicalViewportEditing's
   * revertWithSnapshot is not used (e.g., StepBar's context menu revert).
   */
  const revertStep = useRecoilCallback(
    ({ set, snapshot: recoilSnapshot }) =>
      (stepIndex: number, options?: { silent?: boolean }) => {
        // Read fresh rather than the closure-captured `baseline` (from useRecoilState
        // at the top of the hook) — see createStepWithRevert for why that can be stale.
        const currentBaseline = recoilSnapshot.getLoadable(clinicalStepsBaseline).getValue();
        if (!currentBaseline?.[stepIndex]) return;

        // Capture dirty state before reverting so UNDO can restore it.
        const versioned = recoilSnapshot.getLoadable(steppedHPConfigurations).getValue();
        const modifiedStep = versioned.steps?.[stepIndex]
          ? clone(versioned.steps[stepIndex])
          : null;
        let modifiedViewports: DehydratedViewportsConfigurations | null = null;
        let modifiedLayouts: Layouts | null = null;
        if (currentCaseId) {
          const vpState = recoilSnapshot
            .getLoadable(viewportsConfigurationsState(currentCaseId))
            .getValue();
          modifiedViewports = vpState?.data ? clone(vpState.data) : null;
          const allCaseLayouts = recoilSnapshot.getLoadable(layoutsForAllCasesState).getValue();
          modifiedLayouts = allCaseLayouts[currentCaseId]
            ? clone(allCaseLayouts[currentCaseId])
            : null;
        }

        // Revert step atom to baseline
        set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
          const steps = prev.steps?.map((step, i) => {
            if (i !== stepIndex) return step;
            return clone(currentBaseline[stepIndex]);
          });
          return makeVersionedSteps(steps ?? null, prev);
        });

        // Write directly so the viewport selector reflects the revert
        // immediately (the effect-based chain fires after paint).
        if (currentCaseId) {
          set(
            dehydratedHangingProtocol(currentCaseId),
            stepViewportConfigsToDehydrated(currentBaseline[stepIndex].viewports)
          );
        }

        // Restore layout from the baseline step, and clear the case-scoped override rather than
        // pinning it to this step's layout. The override outranks the step-derived layouts in
        // useLayouts' merge and nothing clears it on step navigation, so setting it here reset the
        // layout of every OTHER step to the one being reverted -- which is what QA re-reported on
        // VX-4768 through "Reset to Original State". Same shape as revertAllStepsToBaseline and
        // resetToHP, which already omit the case's key.
        const baselineLayouts = stepLayoutsToLayouts(currentBaseline[stepIndex].layouts);
        applyProtocolLayouts(set, { caseId: currentCaseId, layouts: baselineLayouts });

        // Clear user viewport overrides
        if (currentCaseId) {
          set(viewportsConfigurationsState(currentCaseId), clearViewportOverrides);
        }

        // Show undo toast (suppressed when called from add-step handler
        // which shows its own contextual toast).
        if (options?.silent) return;
        enqueueToast(CLINICAL_STEP_REVERTED_TOAST.message, {
          ...getDefaultHPToastOptions({ autoHideDuration: HP_TOAST_UNDO_DURATION }),
          ContentComponent: createStepToastContentComponent({
            actionLabel: CLINICAL_STEP_REVERTED_TOAST.actionLabel,
            countdownDuration: HP_TOAST_UNDO_DURATION,
            onAction: () =>
              restoreDirtyStepSnapshot(set, {
                stepIndex,
                currentCaseId,
                modifiedStep,
                modifiedViewports,
                modifiedLayouts,
              }),
          }),
        });
      },
    [currentCaseId, enqueueToast]
  );

  /**
   * Revert ALL steps to their baseline state and cancel any pending
   * deferred saves. Used by "Reset All" to clear all dirty indicators.
   */
  const revertAllStepsToBaseline = useRecoilCallback(
    ({ set, snapshot: recoilSnapshot }) =>
      () => {
        // Read fresh rather than the closure-captured `baseline` (from useRecoilState
        // at the top of the hook) — see createStepWithRevert for why that can be stale.
        const baselineSnapshot = recoilSnapshot.getLoadable(clinicalStepsBaseline).getValue();
        if (!baselineSnapshot) return;

        // Cancel all pending deferred saves
        for (const [stepSmid, { timer }] of pendingSaves) {
          clearTimeout(timer);
          pendingSaves.delete(stepSmid);
        }
        setDeferredSaveCount(0);

        // Cancel any deferred create so it cannot fire after the refetch below and re-add a step this
        // reset was asked to discard. setDeferredSaveCount(0) above already zeroed the counter, so the
        // discard must not decrement it again.
        discardPendingCreates(set, [...pendingCreate.keys()], () => {});

        // Then drop every *unsaved* step, identified by its client-generated smid.
        //
        // Cancelling pendingCreate alone is not enough, because there are two create paths and only one
        // of them is deferred. `createStepWithRevert` (used when a step is dirty) schedules a timer and
        // registers in pendingCreate; `createStepOnBackend` (used when nothing is dirty, which is the
        // state VX-4811's repro reaches by resetting first) fires immediately and registers nowhere. Both
        // write an optimistic step carrying a `step-{uuid}` smid into the baseline, and this function
        // restores the steps atom *from* the baseline -- so the step came back regardless of the timer,
        // and the protocol then also gained the server's copy from the refetch. Two chips, one name.
        //
        // Filtering on the smid covers both paths and any future one, because it keys off what actually
        // makes a step unsaved rather than off which queue happened to know about it.
        const currentBaseline = baselineSnapshot.filter(
          (step) => step.smid != null && !isClientGeneratedSmid(step.smid)
        );
        if (currentBaseline.length !== baselineSnapshot.length) {
          set(clinicalStepsBaseline, clone(currentBaseline));
        }

        // Restore step atom to baseline
        set(steppedHPConfigurations, (prev: VersionedStepConfigurations) =>
          makeVersionedSteps(clone(currentBaseline), prev)
        );

        // Reset to step 0 and restore its viewport state. The sub-step index has to come along: it is
        // per-step, so leaving it where it was points it at sub-steps step 0 may not have. Same
        // reasoning as the clamp in useHangingProtocol, which resets both for the same reason
        // (VX-4783).
        set(hangingProtocolStepIndex, 0);
        set(hangingProtocolSubStepIndex, 0);
        const activeBaseline = currentBaseline[0];
        if (activeBaseline) {
          if (currentCaseId) {
            set(
              dehydratedHangingProtocol(currentCaseId),
              stepViewportConfigsToDehydrated(activeBaseline.viewports)
            );
          }
          const baselineLayouts = stepLayoutsToLayouts(activeBaseline.layouts);
          applyProtocolLayouts(set, { caseId: currentCaseId, layouts: baselineLayouts });
          if (currentCaseId) {
            set(viewportsConfigurationsState(currentCaseId), clearViewportOverrides);
          }
        }
      },
    [currentCaseId]
  );

  /**
   * Pending backend saves, keyed by step smid.
   * The timeout fires the actual PATCH after the undo window expires.
   * Module-level so it survives StepBar remounts.
   */

  /** Fire the actual backend PATCH and update the Apollo cache. */
  const executeSaveOnBackend = useRecoilCallback(
    ({ snapshot }) =>
      async (
        stepSmid: string,
        gqlPayload: ReturnType<typeof stepToGqlInput>,
        rollbackStep: StepConfiguration | null
      ) => {
        pendingSaves.delete(stepSmid);
        try {
          await trackOperation(
            updateStepMutation({
              variables: { stepId: stepSmid, payload: gqlPayload },
            })
          );

          // Update the Apollo cache so resetToHP reads current data.
          const hpSmid = await snapshot.getPromise(activeHangingProtocolSmidState);
          await syncPersistedStepIntoHPCaches(apolloClient, hpSmid, stepSmid, gqlPayload);
        } catch (error) {
          // The optimistic save already advanced the baseline. Roll it back to
          // the pre-save snapshot so the step reads dirty again and the user can
          // retry, rather than silently losing the unsaved-changes indicator.
          if (rollbackStep) {
            setBaseline((prev) =>
              prev ? prev.map((s) => (s.smid === stepSmid ? rollbackStep : s)) : prev
            );
          }
          enqueueToast(
            composeHPFailureMessage({
              action: 'save step',
              fallback: CLINICAL_SAVE_FAILED_TOAST.message,
              error,
            }),
            {
              ...getDefaultHPToastOptions(),
              ContentComponent: createStepToastContentComponent({
                icon: CLINICAL_SAVE_FAILED_TOAST.icon,
              }),
            }
          );
        }
      },
    [updateStepMutation, trackOperation, apolloClient, enqueueToast, setBaseline]
  );

  /** Schedule a backend PATCH after the undo toast window. */
  const scheduleSave = useCallback(
    (
      stepSmid: string,
      gqlPayload: ReturnType<typeof stepToGqlInput>,
      rollbackStep: StepConfiguration | null
    ) => {
      const existing = pendingSaves.get(stepSmid);
      if (existing) {
        // Rescheduling an existing pending save for this step — its timer's
        // decrement is cancelled below, so don't increment the counter again.
        clearTimeout(existing.timer);
      } else {
        setDeferredSaveCount((n) => n + 1);
      }

      const timer = setTimeout(() => {
        setDeferredSaveCount((n) => Math.max(0, n - 1));
        executeSaveOnBackend(stepSmid, gqlPayload, rollbackStep);
      }, HP_TOAST_UNDO_DURATION + 2_000);
      pendingSaves.set(stepSmid, { timer, payload: gqlPayload, rollbackStep });
    },
    [executeSaveOnBackend]
  );

  /** Cancel a pending backend save (called on undo). */
  const cancelPendingSave = useCallback((stepSmid: string) => {
    const pending = pendingSaves.get(stepSmid);
    if (pending) {
      clearTimeout(pending.timer);
      pendingSaves.delete(stepSmid);
      setDeferredSaveCount((n) => Math.max(0, n - 1));
    }
  }, []);

  /** Immediately execute a single pending save (called on toast dismiss). */
  const flushPendingSaveForStep = useCallback(
    (stepSmid: string) => {
      const pending = pendingSaves.get(stepSmid);
      if (pending) {
        clearTimeout(pending.timer);
        setDeferredSaveCount((n) => Math.max(0, n - 1));
        executeSaveOnBackend(stepSmid, pending.payload, pending.rollbackStep);
      }
    },
    [executeSaveOnBackend]
  );

  /**
   * Immediately execute all pending backend saves, clearing their timers. Resolves once every
   * flushed mutation has settled, so a caller that reads the HP back from the server afterwards
   * can order that read behind the writes (VX-4740).
   */
  const flushPendingSaves = useCallback(async (): Promise<void> => {
    // Snapshot entries/count up front: executeSaveOnBackend deletes from
    // pendingSaves synchronously, so reading size afterward would be 0.
    const entries = [...pendingSaves];
    const count = entries.length;
    const settled = entries.map(([stepSmid, { timer, payload, rollbackStep }]) => {
      clearTimeout(timer);
      return executeSaveOnBackend(stepSmid, payload, rollbackStep);
    });
    pendingSaves.clear();
    if (count > 0) setDeferredSaveCount((n) => Math.max(0, n - count));
    await Promise.all(settled);
  }, [executeSaveOnBackend]);

  /**
   * Save a single step via the per-step PATCH mutation. Updates baseline on success.
   *
   * Accepts an optional `stepOverride` for cases where the caller has already
   * constructed the updated step (e.g. rename) and the Recoil atom hasn't
   * committed yet. When omitted, reads the step from the current atom snapshot.
   */
  const saveStep = useRecoilCallback(
    ({ snapshot, set }) =>
      async (stepIndex: number, stepOverride?: StepConfiguration) => {
        let step: StepConfiguration | undefined;

        if (stepOverride) {
          // Use baseline viewport/layout data so that a metadata-only save
          // (e.g. rename) doesn't accidentally commit pending viewport edits.
          // The override's non-viewport fields (display_name, config_details)
          // take precedence over the baseline. Read baseline from the snapshot
          // (not the closure) to get the committed value at call time.
          const currentBaseline = snapshot.getLoadable(clinicalStepsBaseline).getValue();
          const baselineStep = currentBaseline?.[stepIndex];
          step = baselineStep
            ? {
                ...baselineStep,
                display_name: stepOverride.display_name,
                config_details: stepOverride.config_details,
              }
            : stepOverride;
        } else {
          const versioned = await snapshot.getPromise(steppedHPConfigurations);
          const currentSteps = getStepsFromVersioned(versioned);
          step = currentSteps?.[stepIndex];
        }

        // Skip save for steps without a backend smid. Client-generated smids
        // (e.g. "step-1742792077498" from addStep) are not valid backend UUIDs.
        if (!step?.smid || isClientGeneratedSmid(step.smid)) return;

        // Merge the current viewer viewport overrides into the step so that
        // ALL viewports visible in the layout are captured — not just ones
        // explicitly written by writeViewportToStep. Without this, viewport
        // positions populated by the HP match (factory merge) at render time
        // but never clinically edited would be lost on save + reload.
        //
        // viewportsConfigurationsState is case-scoped, not step-scoped -- its contents
        // reflect whichever step is currently ON SCREEN, not necessarily the step being
        // saved (a user can click "Save" on a different, already-dirty chip while viewing
        // another step). Only enrich/clear it when the step being saved is the active one,
        // otherwise this would attach the on-screen step's viewport data to a different
        // step's saved payload and silently discard the on-screen step's own pending state.
        let wasEnriched = false;
        const currentStepIndex = await snapshot.getPromise(hangingProtocolStepIndex);
        if (currentCaseId && !stepOverride && stepIndex === currentStepIndex) {
          const vpState = await snapshot.getPromise(viewportsConfigurationsState(currentCaseId));
          const overrides = vpState?.data;
          if (overrides) {
            const enrichedViewports = { ...step.viewports };
            for (const [viewportId, dehydrated] of Object.entries(overrides)) {
              if (dehydrated == null) continue;
              // Only add viewports that aren't already in the step data.
              // Existing step entries (from writeViewportToStep or the original
              // HP data) may have richer data like criteria.
              if (enrichedViewports[viewportId] != null) continue;
              wasEnriched = true;
              enrichedViewports[viewportId] = {
                display_name: dehydrated.seriesDescriptions?.[0] ?? '',
                study_smid: dehydrated.study?.smid ?? null,
                series_smid: dehydrated.series?.smid ?? null,
                view_type: toAllowedViewportType(dehydrated.viewType),
                series_criteria_source_text: dehydrated.seriesDescriptions?.[0] ?? null,
                comparison_index: dehydrated.comparisonIndex ?? null,
                stack_smid: dehydrated.stack?.smid ?? null,
                viewport_mirror_group_id: dehydrated.viewportMirrorGroupId ?? null,
              };
            }
            if (wasEnriched) {
              step = { ...step, viewports: enrichedViewports };
            }
          }
          // The step now reflects any case-scoped overrides that mattered (either already
          // present in step data, or just folded in above). Clear the case-scoped atom so
          // it doesn't keep re-applying to every other step's rendering after this save
          // (it's not step-scoped -- see generateViewportsConfigurations) or resurface as
          // an untracked edit on a later step navigation/reload (VX-4617).
          set(viewportsConfigurationsState(currentCaseId), clearViewportOverrides);
        }

        const gqlPayload = stepToGqlInput(step);

        // Capture the pre-save baseline BEFORE advancing it. This is the safe
        // save point: the UNDO toast restores it, and a failed deferred save
        // rolls back to it (see executeSaveOnBackend) so the step stays dirty
        // and retryable.
        const preSaveBaseline = snapshot.getLoadable(clinicalStepsBaseline).getValue();
        const preSaveBaselineStep = preSaveBaseline?.[stepIndex]
          ? clone(preSaveBaseline[stepIndex])
          : null;

        // Update baseline immediately so the dirty indicator clears.
        setBaseline((prev) => {
          if (!prev) return prev;
          const next = [...prev];
          next[stepIndex] = clone(step);
          return next;
        });

        // If the step was enriched with viewport overrides, write the
        // enriched data back to the step atom so it matches the baseline.
        if (wasEnriched) {
          set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
            const steps = prev.steps?.map((s, i) => (i === stepIndex ? clone(step!) : s));
            return makeVersionedSteps(steps ?? null, prev);
          });
        }

        if (stepOverride) {
          // Rename auto-saves fire immediately (no undo opportunity).
          try {
            await trackOperation(
              updateStepMutation({
                variables: { stepId: step.smid, payload: gqlPayload },
              })
            );

            // Same cache maintenance the deferred path does. Skipping it was VX-4748: the rename
            // reached the backend, but the precached getBaseViewerData response still held the old
            // display_name, and the next load rehydrated from it and showed the old step name.
            const renameHPSmid = await snapshot.getPromise(activeHangingProtocolSmidState);
            await syncPersistedStepIntoHPCaches(apolloClient, renameHPSmid, step.smid, gqlPayload);
          } catch (err) {
            enqueueToast(
              composeHPFailureMessage({
                action: 'save step',
                fallback: CLINICAL_SAVE_FAILED_TOAST.message,
                error: err,
              }),
              {
                ...getDefaultHPToastOptions(),
                ContentComponent: createStepToastContentComponent({
                  icon: CLINICAL_SAVE_FAILED_TOAST.icon,
                }),
              }
            );
            // Roll the baseline back to its pre-save snapshot so the step returns
            // to a dirty state the user can retry. Without this the optimistic
            // baseline advance above leaves baseline == step, so isStepEqual stays
            // true and the dirty indicator never reappears after a failed rename.
            if (preSaveBaselineStep) {
              setBaseline((prev) => {
                if (!prev) return prev;
                const next = [...prev];
                next[stepIndex] = preSaveBaselineStep;
                return next;
              });
            }
            throw err;
          }
        } else {
          // Explicit saves are deferred until the undo window expires,
          // mirroring the delete pattern. This gives the user time to
          // undo before the mutation fires. The pre-save baseline (captured
          // above) is threaded through so a failed save can roll back to it.
          const stepSmid = step.smid!;
          scheduleSave(stepSmid, gqlPayload, preSaveBaselineStep);

          if (preSaveBaselineStep) {
            const toastKey = `step-saved-${stepSmid}-${Date.now()}`;
            enqueueToast(CLINICAL_STEP_SAVED_TOAST.message, {
              ...getDefaultHPToastOptions({
                toastKey,
                autoHideDuration: HP_TOAST_UNDO_DURATION,
                onClose: () => {
                  flushPendingSaveForStep(stepSmid);
                  closeToast(toastKey);
                },
              }),
              ContentComponent: createStepToastContentComponent({
                actionLabel: CLINICAL_STEP_SAVED_TOAST.actionLabel,
                countdownDuration: HP_TOAST_UNDO_DURATION,
                onAction: () => {
                  cancelPendingSave(stepSmid);
                  setBaseline((prev) => {
                    if (!prev) return prev;
                    const next = [...prev];
                    next[stepIndex] = preSaveBaselineStep;
                    return next;
                  });
                },
              }),
            });
          }
        }
      },
    [
      updateStepMutation,
      setBaseline,
      enqueueToast,
      closeToast,
      trackOperation,
      currentCaseId,
      scheduleSave,
      cancelPendingSave,
      flushPendingSaveForStep,
      apolloClient,
    ]
  );

  /**
   * Re-orders the baseline array to match the post-reorder step order, keeping each step's own
   * last-saved content.
   *
   * Called after a drag-and-drop reorder. It used to be a blanket
   * `setBaseline(clone(currentSteps))`, which made baseline equal current for *every* step and so
   * silently cleared the unsaved-changes dot on any step that had pending edits (VX-4739). Steps are
   * matched by smid and only moved, never rewritten, so a dirty step stays dirty and a clean step
   * stays clean. The array is kept index-aligned with the steps because the revert paths index into
   * it (`currentBaseline[stepIndex]`).
   */
  const reorderBaselineToMatchSteps = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const versioned = await snapshot.getPromise(steppedHPConfigurations);
        const currentSteps = getStepsFromVersioned(versioned);
        const currentBaseline = await snapshot.getPromise(clinicalStepsBaseline);
        if (!currentSteps || !currentBaseline) return;

        const baselineBySmid = new Map(
          currentBaseline.filter((step) => step.smid != null).map((step) => [step.smid, step])
        );
        setBaseline(
          currentSteps.map((step, i) => {
            const matched = step.smid != null ? baselineBySmid.get(step.smid) : undefined;
            // A step with no baseline entry of its own (e.g. added but not yet persisted) keeps
            // whatever sat at its index, matching how the rest of this file treats such steps.
            return clone(matched ?? currentBaseline[i] ?? step);
          })
        );
      },
    [setBaseline]
  );

  /**
   * Persist step order to the backend after a drag-and-drop reorder.
   * Reads the cached HP from Apollo to reconstruct the full update payload,
   * then sends the reordered steps array via UPDATE_HANGING_PROTOCOL.
   */
  const saveStepOrder = useRecoilCallback(
    ({ snapshot, set }) =>
      async (hpSmid: string) => {
        const versioned = await snapshot.getPromise(steppedHPConfigurations);
        const currentSteps = versioned.steps;
        if (!currentSteps || currentSteps.length === 0) return;

        const payloadBase = readHPPayloadBaseFromCache(apolloClient, hpSmid);
        if (!payloadBase) {
          enqueueToast(CLINICAL_REORDER_FAILED_TOAST.message, {
            ...getDefaultHPToastOptions(),
            ContentComponent: createStepToastContentComponent({
              icon: CLINICAL_REORDER_FAILED_TOAST.icon,
            }),
          });
          return;
        }

        // Send each step's last-saved content in the new order. Sending `currentSteps` as-is would
        // commit any unsaved edits sitting in the atom as a side effect of a reorder, so a step the
        // user never saved would come back from the server as saved (VX-4739). Steps with no
        // baseline entry (added during this session) fall back to their current content, which is
        // all the server has to go on for them.
        const orderBaseline = await snapshot.getPromise(clinicalStepsBaseline);
        const baselineBySmid = new Map(
          (orderBaseline ?? [])
            .filter((step) => step.smid != null)
            .map((step) => [step.smid, step] as const)
        );
        const payload: HangingProtocolPayload = {
          ...payloadBase,
          steps: currentSteps.map((step) => {
            const savedStep = step.smid != null ? baselineBySmid.get(step.smid) : undefined;
            return stepToGqlInput(savedStep ?? step);
          }),
        };

        try {
          const result = await trackOperation(
            updateHPMutation({
              variables: { id: hpSmid, payload },
            })
          );
          // Adopt the smids the server just issued. Sending a `steps` array makes it delete and
          // recreate every step row, so the smids in `currentSteps` are now dangling and the next
          // per-step delete or rename would target a row that no longer exists (VX-4773).
          const reorderedSteps = reconcileStepSmids(
            currentSteps,
            result?.data?.updateHangingProtocol?.steps
          );
          // Re-assert the correct order in the viewer atom. The mutation
          // response may update the Apollo cache with steps in a different
          // order, and the BroadcastChannel echo from viewer/1 may overwrite
          // with stale data. This ensures the local atom stays correct.
          set(steppedHPConfigurations, (prev: VersionedStepConfigurations) =>
            makeVersionedSteps(reorderedSteps, prev)
          );
          // The baseline is keyed by smid for dirty comparison and was already reordered to match
          // `currentSteps` before this ran, so it has to take the new smids too.
          set(clinicalStepsBaseline, (prev) =>
            prev != null
              ? reconcileStepSmids(prev, result?.data?.updateHangingProtocol?.steps)
              : prev
          );
          // Invalidate precache so page refresh fetches the new order
          await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });

          // NOTE: broadcastHPInvalidation() is intentionally omitted here, for the same reason it is
          // omitted in `createStepOnBackend` above.
          //
          // The payload built above deliberately ships each step's *baseline* content so a reorder
          // doesn't commit unsaved edits. Broadcasting an invalidation made the other viewer window
          // refetch that now-clean server state, re-initialize `steppedHPConfigurations` from it, and
          // broadcast the higher-versioned result back here -- overwriting the dirty step with its
          // saved content. The step then genuinely equalled its baseline, so its unsaved-changes dot
          // disappeared: VX-4739 all over again, this time via the cross-window round trip.
          //
          // The other window does not need the broadcast to learn the new order:
          // `steppedHPConfigurations` is BroadcastChannel-synced and was re-asserted just above, and
          // the precache delete covers the next page load.
        } catch (error) {
          enqueueToast(
            composeHPFailureMessage({
              action: 'save step order',
              fallback: CLINICAL_REORDER_FAILED_TOAST.message,
              error,
            }),
            {
              ...getDefaultHPToastOptions(),
              ContentComponent: createStepToastContentComponent({
                icon: CLINICAL_REORDER_FAILED_TOAST.icon,
              }),
            }
          );
        }
      },
    [apolloClient, updateHPMutation, enqueueToast, trackOperation]
  );

  const [isCreatingStep, setIsCreatingStep] = useRecoilState(isCreatingStepState);
  // Synchronous re-entry lock for createStepOnBackend. The `isCreatingStep` Recoil atom drives the
  // button's `disabled` attribute for UI/Playwright actionability, but the closure-captured value
  // inside the recoil callback lags a render behind the atom, so two synchronous clicks can both
  // slip past `if (isCreatingStep) return;` before React commits the new render. That manifests as
  // either duplicate `Step N` labels (both clicks proceed) or a silent no-op (second click catches
  // a stale closure that still reads `true`). A ref is updated imperatively and observed
  // synchronously, which makes the guard race-free regardless of render timing.
  const isCreatingStepLockRef = useRef(false);

  /**
   * Creates a new step on the backend immediately, cloning the current step,
   * and updates local Recoil state with the real backend smid.
   * Used in clinical mode so new steps always have a valid backend UUID.
   */
  const createStepOnBackend = useRecoilCallback(
    ({ snapshot, set }) =>
      async (hpSmid: string, sourceStepOverride?: StepConfiguration) => {
        if (isCreatingStepLockRef.current) return;
        isCreatingStepLockRef.current = true;
        setIsCreatingStep(true);

        // The lock + creating flag are always released in `finally`, so every
        // exit path (early return, mutation failure, accepted-but-no-smid)
        // leaves the button re-enabled and the re-entry guard cleared.
        try {
          const versioned = await snapshot.getPromise(steppedHPConfigurations);
          const currentSteps = versioned.steps;
          const currentIndex = await snapshot.getPromise(hangingProtocolStepIndex);
          if (!currentSteps) return;

          const sourceStep = sourceStepOverride ?? currentSteps[currentIndex];
          const newStepName = generateDefaultStepName(currentSteps);

          // Omit `smid` so `createDefaultStep` generates a client-side
          // `step-{uuid}` identifier. A null smid would block the rename handler
          // (`StepBar.tsx` guards on `step.smid != null`) and defeat smid-based
          // baseline tracking in `useClinicalStepDirtyState`.
          const newStep = createDefaultStep({
            name: newStepName,
            sourceStep,
            defaultLayout: '2,2',
          });

          // Optimistically add the step to both state and baseline so the UI
          // updates immediately without showing a dirty indicator. The temporary
          // client-generated smid will be replaced with the real backend smid
          // when the mutation resolves.
          const tempSmid = newStep.smid;
          set(steppedHPConfigurations, (prev: VersionedStepConfigurations) =>
            makeVersionedSteps(prev.steps ? [...prev.steps, newStep] : [newStep], prev)
          );
          set(clinicalStepsBaseline, (prev) =>
            prev ? [...prev, clone(newStep)] : [clone(newStep)]
          );
          set(hangingProtocolStepIndex, currentSteps.length);

          const gqlPayload = stepToGqlInput(newStep);

          // Tell useHangingProtocol not to re-initialize steps from the server until this resolves;
          // until then a fetched protocol cannot know about this step (VX-4811). Set immediately
          // above the try/catch so nothing between here and the mutation can throw past the matching
          // `endPendingStepCreate` calls and leave the guard stuck for the life of the window.
          beginPendingStepCreate(tempSmid);

          // Only the mutation itself should trigger the rollback + error toast.
          // Post-mutation side effects (cache update, broadcast, success toast)
          // must not propagate failures as "Failed to add step" — the server
          // already accepted the step.
          let result: Awaited<ReturnType<typeof createStepMutation>>;
          try {
            result = await trackOperation(
              createStepMutation({
                variables: { hpId: hpSmid, payload: gqlPayload },
              })
            );
          } catch (error) {
            endPendingStepCreate(tempSmid);
            // Roll back the optimistic add from both state and baseline
            set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
              const rolled = prev.steps?.filter((s) => s.smid !== tempSmid) ?? null;
              return makeVersionedSteps(rolled, prev);
            });
            set(clinicalStepsBaseline, (prev) => prev?.filter((s) => s.smid !== tempSmid) ?? null);
            set(hangingProtocolStepIndex, currentIndex);
            enqueueToast(
              composeHPFailureMessage({
                action: 'create step',
                fallback: CREATE_FAILED_TOAST.message,
                error,
              }),
              {
                ...getDefaultHPToastOptions(),
                ContentComponent: createStepToastContentComponent({
                  icon: CREATE_FAILED_TOAST.icon,
                }),
              }
            );
            return;
          }

          const createdStep = result.data?.createHangingProtocolStep;
          const createdSmid = createdStep?.smid;
          // Cleared whether or not the server returned a usable smid: leaving it set would block
          // every later re-initialization for the life of the window.
          endPendingStepCreate(tempSmid);
          if (!createdSmid) return;

          // Replace the temporary smid with the real backend smid in both
          // state and baseline.
          set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
            const updated = prev.steps?.map((s) =>
              s.smid === tempSmid ? { ...s, smid: createdSmid } : s
            );
            return makeVersionedSteps(updated ?? null, prev);
          });
          set(
            clinicalStepsBaseline,
            (prev) =>
              prev?.map((s) => (s.smid === tempSmid ? { ...s, smid: createdSmid } : s)) ?? null
          );

          // Append the new step to the cached getBaseViewerData entries so
          // page reloads serve the up-to-date HP without a full network fetch.
          // Any failure here should NOT roll back the successful create.
          try {
            await updatePrecacheQuery({
              query: GET_BASE_VIEWER_DATA,
              transform: (data) => appendStepToMatchedHP(data, hpSmid, createdStep),
            });
          } catch (err) {
            logger.warn('Failed to update precache after create step', { err });
          }

          // NOTE: broadcastHPInvalidation() is intentionally omitted here.
          // steppedHPConfigurations already propagates the new step to viewer/1
          // via broadcastChannelSynchronizerEffect. Triggering a full HP refetch
          // on viewer/1 causes it to re-initialize steppedHPConfigurations with
          // stale server data (step not yet reflected), then broadcast a
          // higher-versioned result back to viewer/0 — overwriting the optimistic
          // add and producing duplicate/missing step names under rapid creation.

          enqueueToast(CLINICAL_STEP_CREATED_TOAST.message, {
            ...getDefaultHPToastOptions(),
            ContentComponent: createStepToastContentComponent({}),
          });
        } finally {
          isCreatingStepLockRef.current = false;
          setIsCreatingStep(false);
        }
      },
    [createStepMutation, enqueueToast, setIsCreatingStep, trackOperation]
  );

  /**
   * Fire the create mutation and swap the temp smid for the real one.
   *
   * Reads the current step from `steppedHPConfigurations` by `tempSmid` at
   * fire time so that any edits (rename, viewport changes) made during the
   * undo window are included in the payload. Using a closure-captured payload
   * here would send stale data to the backend.
   */
  const executeCreateOnBackend = useRecoilCallback(
    ({ snapshot, set }) =>
      async (tempSmid: string, hpId: string, preCreateIndex: number) => {
        pendingCreate.delete(tempSmid);

        // Rebuild the payload from the current atom state, not a stale closure.
        const versioned = await snapshot.getPromise(steppedHPConfigurations);
        const currentStep = versioned.steps?.find((s) => s.smid === tempSmid);
        if (!currentStep) return;
        const gqlPayload = stepToGqlInput(currentStep);

        beginPendingStepCreate(tempSmid);

        // Only the mutation should trigger the rollback + error toast.
        // Post-mutation side effects (cache update, broadcast) must not
        // propagate failures as a create error when the server accepted.
        let result: Awaited<ReturnType<typeof createStepMutation>>;
        try {
          result = await trackOperation(
            createStepMutation({ variables: { hpId, payload: gqlPayload } })
          );
        } catch (error) {
          endPendingStepCreate(tempSmid);
          // Roll back
          set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
            const rolled = prev.steps?.filter((s) => s.smid !== tempSmid) ?? null;
            return makeVersionedSteps(rolled, prev);
          });
          set(clinicalStepsBaseline, (prev) => prev?.filter((s) => s.smid !== tempSmid) ?? null);
          // The step index was optimistically advanced to the new step's position when it was
          // added. Restore it to the pre-create index so it does not point at the now-removed
          // step (matches createStepOnBackend's non-deferred rollback). VX-4441.
          set(hangingProtocolStepIndex, preCreateIndex);
          enqueueToast(
            composeHPFailureMessage({
              action: 'create step',
              fallback: CREATE_FAILED_TOAST.message,
              error,
            }),
            {
              ...getDefaultHPToastOptions(),
              ContentComponent: createStepToastContentComponent({
                icon: CREATE_FAILED_TOAST.icon,
              }),
            }
          );
          return;
        }

        const createdStep = result.data?.createHangingProtocolStep;
        const createdSmid = createdStep?.smid;
        // Cleared whether or not the server returned a usable smid: leaving it set would block
        // every later re-initialization for the life of the window.
        endPendingStepCreate(tempSmid);
        if (!createdSmid) return;

        // Swap temp smid → real smid in the atom, preserving any subsequent
        // local edits.
        set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
          const updated = prev.steps?.map((s) =>
            s.smid === tempSmid ? { ...s, smid: createdSmid } : s
          );
          return makeVersionedSteps(updated ?? null, prev);
        });
        // Update baseline to reflect what we just sent to the backend. Using
        // `currentStep` (the value we actually persisted) keeps baseline in
        // sync with server state so there is no false dirty indicator.
        set(clinicalStepsBaseline, (prev) => {
          const synced = { ...currentStep, smid: createdSmid };
          if (!prev) return [clone(synced)];
          const hasEntry = prev.some((s) => s.smid === tempSmid);
          return hasEntry
            ? prev.map((s) => (s.smid === tempSmid ? clone(synced) : s))
            : [...prev, clone(synced)];
        });

        // Append the new step to the cached getBaseViewerData entries so
        // page reloads serve the up-to-date HP without evicting the cache.
        // Any failure here should NOT roll back the successful create.
        // Write `createdStep` as-is: `gqlPayload` above was rebuilt from
        // `currentStep` at fire time, so the server echo already reflects any
        // rename or viewport edit made during the undo window. Spreading
        // `currentStep` over it instead would overwrite the GQL array fields
        // (`layouts: [{window_id, layout}]`, `viewports: [{viewport_id, ...}]`)
        // with the frontend's keyed-record form, which Apollo rejects field by
        // field ("Missing field 'window_id' while writing result"). That wrote a
        // malformed HP into the precache and left the viewer blank on the next
        // reload. Matches the non-deferred path in `createStepOnBackend`.
        try {
          await updatePrecacheQuery({
            query: GET_BASE_VIEWER_DATA,
            transform: (data) =>
              appendStepToMatchedHP(data, hpId, { ...createdStep, smid: createdSmid }),
          });
        } catch (err) {
          logger.warn('Failed to update precache after deferred create step', { err });
        }

        // NOTE: broadcastHPInvalidation() is intentionally omitted here.
        // steppedHPConfigurations already propagates the new step to viewer/1 via
        // broadcastChannelSynchronizerEffect. Triggering a full HP refetch on viewer/1 causes it
        // to re-initialize steppedHPConfigurations with stale server data (step not yet
        // reflected), then broadcast a higher-versioned result back to viewer/0 — overwriting
        // the optimistic add and producing duplicate/missing step names under rapid creation.
      },
    [createStepMutation, enqueueToast, trackOperation]
  );

  /**
   * Add a new step when the current step has unsaved changes. Reverts the
   * current step, optimistically adds the new step, and shows an undo toast.
   * The actual create mutation fires when the toast dismisses. Undo restores
   * the dirty state and removes the new step.
   */
  const createStepWithRevert = useRecoilCallback(
    ({ snapshot, set }) =>
      (hpSmid: string) => {
        const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
        const currentSteps = versioned.steps;
        const currentIndex = snapshot.getLoadable(hangingProtocolStepIndex).getValue();
        if (!currentSteps) return;

        // Read fresh rather than using the closure-captured `baseline` (from
        // useRecoilState at the top of the hook): that value can lag one render
        // behind the atom if a save/delete elsewhere updates the baseline right
        // as this fires, causing the revert below to write a step that doesn't
        // match the *current* baseline — leaving the dirty indicator stuck on
        // forever since nothing else will re-trigger the comparison.
        const currentBaseline = snapshot.getLoadable(clinicalStepsBaseline).getValue();

        // Every dirty step is reverted, not just the active one. The confirmation modal that leads
        // here is gated on "any step has unsaved changes", so reverting only the active step left
        // the edits (and the yellow dot) in place whenever the dirty step was one the user had
        // already navigated away from (VX-4734).
        const dirtyIndices: number[] = [];
        if (currentBaseline) {
          for (let i = 0; i < currentSteps.length; i++) {
            const baselineStep = currentBaseline[i];
            if (baselineStep != null && !isStepEqual(currentSteps[i], baselineStep)) {
              dirtyIndices.push(i);
            }
          }
        }
        const currentStepIsDirty = dirtyIndices.includes(currentIndex);

        // Capture each dirty step's edits so UNDO can put them all back.
        const modifiedStepsByIndex = new Map<number, StepConfiguration>();
        for (const i of dirtyIndices) {
          if (currentSteps[i]) modifiedStepsByIndex.set(i, clone(currentSteps[i]));
        }

        if (dirtyIndices.length > 0) {
          const dirtySet = new Set(dirtyIndices);
          set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
            const steps = prev.steps?.map((step, i) =>
              dirtySet.has(i) ? clone(currentBaseline![i]) : step
            );
            return makeVersionedSteps(steps ?? null, prev);
          });
        }

        // The viewport/layout atoms describe what is on screen, so they only need restoring when
        // the step being displayed was one of the reverted ones.
        let modifiedStep: StepConfiguration | null = null;
        let modifiedViewports: DehydratedViewportsConfigurations | null = null;
        let modifiedLayouts: Layouts | null = null;
        if (currentStepIsDirty) {
          modifiedStep = modifiedStepsByIndex.get(currentIndex) ?? null;
          if (currentCaseId) {
            const vpState = snapshot
              .getLoadable(viewportsConfigurationsState(currentCaseId))
              .getValue();
            modifiedViewports = vpState?.data ? clone(vpState.data) : null;
            const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
            modifiedLayouts = allCaseLayouts[currentCaseId]
              ? clone(allCaseLayouts[currentCaseId])
              : null;
          }

          if (currentCaseId) {
            set(
              dehydratedHangingProtocol(currentCaseId),
              stepViewportConfigsToDehydrated(currentBaseline![currentIndex].viewports)
            );
          }
          const baselineLayouts = stepLayoutsToLayouts(currentBaseline![currentIndex].layouts);
          // `modifiedLayouts` was captured above, so UNDO can still put the dirty layout back.
          applyProtocolLayouts(set, { caseId: currentCaseId, layouts: baselineLayouts });
          if (currentCaseId) {
            set(viewportsConfigurationsState(currentCaseId), clearViewportOverrides);
          }
        }

        // Build and optimistically add the new step (using modified data as source).
        // Omit `smid` so `createDefaultStep` generates a client-side
        // `step-{uuid}` identifier. See matching note in `createStepOnBackend`.
        const sourceStep = modifiedStep ?? currentSteps[currentIndex];
        const newStepName = generateDefaultStepName(currentSteps);
        const newStep = createDefaultStep({
          name: newStepName,
          sourceStep,
          defaultLayout: '2,2',
        });
        const tempSmid = newStep.smid!;

        set(steppedHPConfigurations, (prev: VersionedStepConfigurations) =>
          makeVersionedSteps(prev.steps ? [...prev.steps, newStep] : [newStep], prev)
        );
        set(clinicalStepsBaseline, (prev) => (prev ? [...prev, clone(newStep)] : [clone(newStep)]));
        set(hangingProtocolStepIndex, currentSteps.length);

        // The active step is now the one just created, so what the viewer publishes has to describe
        // *it*.
        //
        // Add Step is a clone action: pressing it with an unsaved layout on screen should carry that
        // layout onto the new step, and `sourceStep` above does exactly that. But the revert block
        // published the SOURCE step's baseline -- correct while the source was still on screen -- and
        // nothing re-published when the index moved here. So the new step rendered on the source's
        // last-saved grid while its stored layouts held the cloned one, and the reader saw the change
        // they had just made silently disappear (VX-4768).
        //
        // applyProtocolLayouts rather than a bare publish: the step owns this layout, so the
        // case-scoped override must be cleared in the same breath or it outranks every later step.
        applyProtocolLayouts(set, {
          caseId: currentCaseId,
          layouts: stepLayoutsToLayouts(newStep.layouts),
        });
        if (currentCaseId) {
          set(
            dehydratedHangingProtocol(currentCaseId),
            stepViewportConfigsToDehydrated(newStep.viewports)
          );
        }

        // Schedule deferred create. The payload is rebuilt at fire time inside
        // `executeCreateOnBackend` so that any rename or viewport edits made
        // during the undo window are included.
        setDeferredSaveCount((n) => n + 1);
        const toastKey = `step-create-revert-${tempSmid}-${Date.now()}`;
        const timer = setTimeout(() => {
          setDeferredSaveCount((n) => Math.max(0, n - 1));
          executeCreateOnBackend(tempSmid, hpSmid, currentIndex);
        }, HP_TOAST_UNDO_DURATION + 2_000);
        pendingCreate.set(tempSmid, { timer, hpSmid, preCreateIndex: currentIndex });

        // Offer UNDO whenever something was actually discarded, which is now any dirty step rather
        // than only the active one (VX-4734).
        const toastDef =
          dirtyIndices.length > 0
            ? CLINICAL_STEP_CREATED_WITH_REVERT_TOAST
            : CLINICAL_STEP_CREATED_TOAST;
        enqueueToast(toastDef.message, {
          ...getDefaultHPToastOptions({
            toastKey,
            autoHideDuration: HP_TOAST_UNDO_DURATION,
            onClose: () => {
              // Flush: fire create immediately
              const pending = pendingCreate.get(tempSmid);
              if (pending) {
                clearTimeout(pending.timer);
                pendingCreate.delete(tempSmid);
                setDeferredSaveCount((n) => Math.max(0, n - 1));
                executeCreateOnBackend(tempSmid, hpSmid, currentIndex);
              }
              closeToast(toastKey);
            },
          }),
          ContentComponent: createStepToastContentComponent({
            actionLabel: toastDef.actionLabel,
            icon: toastDef.icon,
            countdownDuration: toastDef.actionLabel ? HP_TOAST_UNDO_DURATION : undefined,
            onAction: () => {
              // Cancel the deferred create and drop the optimistic step from both atoms. Shared with
              // Reset All so the two discard paths cannot disagree about what undoing a create means.
              discardPendingCreates(set, [tempSmid], (count) =>
                setDeferredSaveCount((n) => Math.max(0, n - count))
              );

              // Restore step index to the original position
              set(hangingProtocolStepIndex, currentIndex);

              // Put back every step that was reverted, not just the active one, so UNDO is the
              // exact inverse of the discard (VX-4734).
              const otherModifiedSteps = Array.from(modifiedStepsByIndex.entries()).filter(
                ([index]) => index !== currentIndex
              );
              if (otherModifiedSteps.length > 0) {
                set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
                  const restored = new Map(otherModifiedSteps);
                  const steps = prev.steps?.map((step, i) => restored.get(i) ?? step);
                  return makeVersionedSteps(steps ?? null, prev);
                });
              }

              // Restore the active step's data, viewport overrides, and layouts
              restoreDirtyStepSnapshot(set, {
                stepIndex: currentIndex,
                currentCaseId,
                modifiedStep,
                modifiedViewports,
                modifiedLayouts,
              });
            },
          }),
        });
      },
    [currentCaseId, enqueueToast, closeToast, executeCreateOnBackend]
  );

  /**
   * Pending backend deletions, keyed by step smid.
   * The timeout fires the actual DELETE after the undo window expires.
   * If undo is triggered, call cancelPendingDeletion to cancel the timeout.
   * Module-level so it survives StepBar remounts.
   */

  /** Fire the actual backend DELETE and update the baseline. */
  const executeDeleteOnBackend = useRecoilCallback(
    ({ set }) =>
      async (stepSmid: string) => {
        pendingDeletions.delete(stepSmid);
        pendingDeletionBaselines.delete(stepSmid);
        // The mutation is about to fire, so the server is about to agree with us either way.
        endPendingStepDelete(stepSmid);
        try {
          await trackOperation(deleteStepMutation({ variables: { stepId: stepSmid } }));
          set(clinicalStepsBaseline, (prev) => {
            if (!prev) return prev;
            return prev.filter((s) => s.smid !== stepSmid);
          });
          await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });
        } catch (error) {
          enqueueToast(
            composeHPFailureMessage({
              action: 'delete step',
              fallback: DELETE_FAILED_TOAST.message,
              error,
            }),
            {
              ...getDefaultHPToastOptions(),
              ContentComponent: createStepToastContentComponent({
                icon: DELETE_FAILED_TOAST.icon,
              }),
            }
          );
        }
      },
    [deleteStepMutation, enqueueToast, trackOperation]
  );

  /**
   * Schedule a backend DELETE after the undo toast window (30s).
   * Call this when a step is deleted locally. If the user clicks undo,
   * call cancelPendingDeletion to prevent the backend call.
   */
  const scheduleDeletion = useRecoilCallback(
    ({ set }) =>
      (stepSmid: string) => {
        if (isClientGeneratedSmid(stepSmid)) return;
        // Clear any existing timer for this smid (e.g. rapid delete/undo/delete)
        const existing = pendingDeletions.get(stepSmid);
        if (existing) clearTimeout(existing);

        // Immediately sync baseline so dirty indicators don't appear on
        // remaining steps while waiting for the backend delete. Stash the
        // step's own baseline entry first so an undo can restore it exactly (VX-4625).
        set(clinicalStepsBaseline, (prev) => {
          pendingDeletionBaselines.set(stepSmid, prev?.find((s) => s.smid === stepSmid) ?? null);
          if (!prev) return prev;
          return prev.filter((s) => s.smid !== stepSmid);
        });

        const timer = setTimeout(() => {
          executeDeleteOnBackend(stepSmid);
        }, HP_TOAST_UNDO_DURATION + 2_000); // Slightly longer than the toast auto-hide
        pendingDeletions.set(stepSmid, timer);
        // Tell every window the step is on its way out, so a peer refetching during the undo window
        // reads "still on the server" as a deletion in progress rather than as its own staleness.
        beginPendingStepDelete(stepSmid);
      },
    [executeDeleteOnBackend]
  );

  /**
   * Cancel a pending backend deletion (called on undo) and restore the
   * step's pre-delete baseline entry, so its dirty state reflects whatever
   * it was before the delete rather than always coming back clean (VX-4625).
   *
   * By the time this runs, the caller (StepBar's handleUndoDelete) has already restored the
   * full pre-delete step array into steppedHPConfigurations, with the undone step back at its
   * original index. revertStep/revertAllStepsToBaseline read baseline[stepIndex] positionally
   * against that array, so the restored baseline entry must be spliced back into the same index
   * rather than appended -- appending would misalign every step after the undone one whenever
   * the deleted step wasn't already last.
   */
  const cancelPendingDeletion = useRecoilCallback(
    ({ snapshot }) =>
      async (stepSmid: string) => {
        const timer = pendingDeletions.get(stepSmid);
        if (timer) {
          clearTimeout(timer);
          pendingDeletions.delete(stepSmid);
        }
        endPendingStepDelete(stepSmid);
        if (pendingDeletionBaselines.has(stepSmid)) {
          const preDeleteEntry = pendingDeletionBaselines.get(stepSmid);
          pendingDeletionBaselines.delete(stepSmid);
          if (!preDeleteEntry) return;

          const versioned = await snapshot.getPromise(steppedHPConfigurations);
          const currentSteps = getStepsFromVersioned(versioned);
          const restoredIndex = currentSteps?.findIndex((s) => s.smid === stepSmid) ?? -1;

          setBaseline((prev) => {
            const entry = clone(preDeleteEntry);
            if (!prev) return [entry];
            if (restoredIndex < 0 || restoredIndex > prev.length) return [...prev, entry];
            return [...prev.slice(0, restoredIndex), entry, ...prev.slice(restoredIndex)];
          });
        }
      },
    [setBaseline]
  );

  /** Immediately execute a single pending deletion. */
  const flushPendingDeletionForStep = useCallback(
    (stepSmid: string) => {
      const timer = pendingDeletions.get(stepSmid);
      if (timer) {
        clearTimeout(timer);
        executeDeleteOnBackend(stepSmid);
      }
    },
    [executeDeleteOnBackend]
  );

  /**
   * Immediately execute all pending backend deletions, clearing their timers. Resolves once every
   * flushed deletion has settled, for the same ordering reason as `flushPendingSaves`.
   */
  const flushPendingDeletions = useCallback(async (): Promise<void> => {
    const settled = [...pendingDeletions].map(([stepSmid, timer]) => {
      clearTimeout(timer);
      return executeDeleteOnBackend(stepSmid);
    });
    pendingDeletions.clear();
    pendingDeletionBaselines.clear();
    await Promise.all(settled);
  }, [executeDeleteOnBackend]);

  /** Immediately fire all pending deferred creates, clearing their timers. */
  const flushPendingCreate = useCallback(() => {
    if (pendingCreate.size === 0) return;
    for (const [tempSmid, { timer, hpSmid, preCreateIndex }] of pendingCreate) {
      clearTimeout(timer);
      setDeferredSaveCount((n) => Math.max(0, n - 1));
      executeCreateOnBackend(tempSmid, hpSmid, preCreateIndex);
    }
    pendingCreate.clear();
  }, [executeCreateOnBackend]);

  // If the page navigates away during an undo window, fire any deferred backend
  // writes immediately (best-effort) so the user's pending save/create/delete is
  // not silently dropped when the deferred timer never gets to run.
  useOnUnload(() => {
    // Nothing to await on unload -- the page is going away, so these are dispatched best-effort.
    void flushPendingSaves();
    void flushPendingDeletions();
    flushPendingCreate();
  });

  return {
    revertStep,
    saveStep,
    saveStepOrder,
    reorderBaselineToMatchSteps,
    revertAllStepsToBaseline,
    createStepOnBackend,
    createStepWithRevert,
    isCreatingStep,
    scheduleDeletion,
    cancelPendingDeletion,
    flushPendingDeletionForStep,
    flushPendingDeletions,
    flushPendingSaves,
    pendingOperations,
    deferredSaveCount,
  };
}
